/**
 * 전역 검색 진입점 상태 (H3 — 단축키·사이드바·TopBar 공유)
 *
 * Cmd+K / Ctrl+K → GlobalSearchModal · TopBar inline input click → focusInlineSearch
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface GlobalSearchContextValue {
  open: boolean;
  query: string;
  openSearch: (initialQuery?: string) => void;
  closeSearch: () => void;
  setQuery: (query: string) => void;
  registerSearchInputFocus: (focus: () => void) => void;
  /** TopBar inline GlobalSearchInput — distinct from Cmd+K modal entry. */
  focusInlineSearch: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null);

export interface GlobalSearchProviderProps {
  children: ReactNode;
}

export const GlobalSearchProvider: React.FC<GlobalSearchProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const focusSearchInputRef = useRef<(() => void) | null>(null);

  const registerSearchInputFocus = useCallback((focus: () => void) => {
    focusSearchInputRef.current = focus;
  }, []);

  const focusInlineSearch = useCallback(() => {
    focusSearchInputRef.current?.();
  }, []);

  const openSearch = useCallback((initialQuery?: string) => {
    const trimmed = initialQuery?.trim() ?? '';
    setQuery(trimmed);
    setOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const value = useMemo(
    () => ({
      open,
      query,
      openSearch,
      closeSearch,
      setQuery,
      registerSearchInputFocus,
      focusInlineSearch,
    }),
    [open, query, openSearch, closeSearch, registerSearchInputFocus, focusInlineSearch]
  );

  return <GlobalSearchContext.Provider value={value}>{children}</GlobalSearchContext.Provider>;
};

export function useGlobalSearch(): GlobalSearchContextValue {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error('useGlobalSearch must be used within GlobalSearchProvider');
  }
  return ctx;
}
